import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Video, Plus, Search, Trash2, Eye, Play, Square, Users, Calendar,
  Link2, Mail, Download, Clock, MessageSquare, BarChart3, Send, ExternalLink,
  TrendingUp, Loader2, Sparkles, PieChart, Repeat, SplitSquareHorizontal, ArrowLeftRight,
  Upload, Film, CloudOff, Cloud, X, FileText, Mic, ChevronDown, ChevronUp, ListChecks, Quote, Tag,
  MessageCircle, CheckCircle, ThumbsUp, Heart, Flame, Scissors, Share2
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WebinarsPage = () => {
  const { token, user } = useAuth();
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isLiveOpen, setIsLiveOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [polls, setPolls] = useState([]);
  const chatRef = useRef(null);
  const chatPollRef = useRef(null);

  const [form, setForm] = useState({
    title: '', description: '', scheduled_at: '', duration_minutes: 60,
    max_attendees: 100, waiting_room: false, recording_enabled: false, tags: [],
    is_recurring: false, recurrence_rule: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [viewTab, setViewTab] = useState('details');
  const [templates, setTemplates] = useState([]);
  const [isTemplateListOpen, setIsTemplateListOpen] = useState(false);
  const [liveStats, setLiveStats] = useState(null);
  const liveStatsInterval = useRef(null);
  const [breakoutRooms, setBreakoutRooms] = useState([]);
  const [isBreakoutOpen, setIsBreakoutOpen] = useState(false);
  const [breakoutForm, setBreakoutForm] = useState({ name: '', duration_minutes: 15, assigned_user_ids: [] });
  const [allUsers, setAllUsers] = useState([]);
  const [qaQuestions, setQaQuestions] = useState({ pinned: [], active: [], answered: [], dismissed: [], total: 0 });
  const [qaInput, setQaInput] = useState('');
  const [qaAnonymous, setQaAnonymous] = useState(false);
  const [answerInput, setAnswerInput] = useState('');
  const [answeringId, setAnsweringId] = useState(null);
  const [sidebarTab, setSidebarTab] = useState('chat');
  const [floatingReactions, setFloatingReactions] = useState([]);
  const reactionTimeoutRef = useRef(null);
  const [recordings, setRecordings] = useState([]);
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const [recordForm, setRecordForm] = useState({ title: '', description: '', url: '', duration_minutes: 0 });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [playingRecording, setPlayingRecording] = useState(null);
  const [transcriptData, setTranscriptData] = useState(null);
  const [transcribing, setTranscribing] = useState({});
  const [showTranscript, setShowTranscript] = useState(false);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const [annotations, setAnnotations] = useState([]);
  const [annotationInput, setAnnotationInput] = useState('');
  const [annotationType, setAnnotationType] = useState('comment');
  const [replyInputs, setReplyInputs] = useState({});
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [clips, setClips] = useState([]);
  const [isClipOpen, setIsClipOpen] = useState(false);
  const [clipForm, setClipForm] = useState({ title: '', description: '', start_seconds: 0, end_seconds: 0, annotation_id: '', tags: [] });
  const [clipTagInput, setClipTagInput] = useState('');
  const [playingClip, setPlayingClip] = useState(null);

  useEffect(() => { fetchWebinars(); fetchTemplates(); fetchAllUsers(); }, [search, statusFilter]);

  const fetchWebinars = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/webinars`, { params, headers: { Authorization: `Bearer ${token}` } });
      setWebinars(res.data);
    } catch { toast.error('Failed to fetch webinars'); }
    finally { setLoading(false); }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/webinar-templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(res.data);
    } catch { /* silent */ }
  };

  const handleCreateFromTemplate = async (template) => {
    setForm({
      title: '', description: template.description || '', scheduled_at: '',
      duration_minutes: template.duration_minutes || 60,
      max_attendees: template.max_attendees || 100,
      waiting_room: template.waiting_room || false,
      recording_enabled: template.recording_enabled || false,
      tags: template.tags || [],
    });
    setIsTemplateListOpen(false);
    setIsCreateOpen(true);
    toast.info(`Loaded template: ${template.name}. Set title and date to create.`);
  };

  const handleSaveAsTemplate = async (webinarId) => {
    try {
      await axios.post(`${API}/webinar-templates/from-webinar/${webinarId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Saved as template');
      fetchTemplates();
    } catch { toast.error('Failed to save template'); }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/webinar-templates/${templateId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const startLivePolling = (webinarId) => {
    if (liveStatsInterval.current) clearInterval(liveStatsInterval.current);
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API}/webinars/${webinarId}/live-stats`, { headers: { Authorization: `Bearer ${token}` } });
        setLiveStats(res.data);
      } catch { /* silent */ }
    };
    fetchLive();
    liveStatsInterval.current = setInterval(fetchLive, 10000);
  };

  const stopLivePolling = () => {
    if (liveStatsInterval.current) { clearInterval(liveStatsInterval.current); liveStatsInterval.current = null; }
    setLiveStats(null);
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(res.data);
    } catch { /* silent */ }
  };

  const fetchBreakoutRooms = async (webinarId) => {
    try {
      const res = await axios.get(`${API}/webinars/${webinarId}/breakout-rooms`, { headers: { Authorization: `Bearer ${token}` } });
      setBreakoutRooms(res.data.rooms || []);
    } catch { /* silent */ }
  };

  const handleCreateBreakout = async () => {
    if (!breakoutForm.name || !selected) { toast.error('Room name is required'); return; }
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/breakout-rooms`, breakoutForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Breakout room "${breakoutForm.name}" created`);
      setBreakoutForm({ name: '', duration_minutes: 15, assigned_user_ids: [] });
      setIsBreakoutOpen(false);
      fetchBreakoutRooms(selected.webinar_id);
    } catch { toast.error('Failed to create breakout room'); }
  };

  const handleEndBreakout = async (roomId) => {
    if (!selected) return;
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/breakout-rooms/${roomId}/end`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Breakout room ended');
      fetchBreakoutRooms(selected.webinar_id);
    } catch { toast.error('Failed to end breakout'); }
  };

  const handleEndAllBreakouts = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/breakout-rooms/end-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('All breakout rooms ended — attendees returning to main session');
      fetchBreakoutRooms(selected.webinar_id);
    } catch { toast.error('Failed to end breakouts'); }
  };

  const fetchQA = async (webinarId) => {
    try {
      const res = await axios.get(`${API}/webinars/${webinarId}/qa`, { headers: { Authorization: `Bearer ${token}` } });
      setQaQuestions(res.data);
    } catch { /* silent */ }
  };

  const handleSubmitQuestion = async () => {
    if (!qaInput.trim() || !selected) return;
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/qa`, { content: qaInput, is_anonymous: qaAnonymous }, { headers: { Authorization: `Bearer ${token}` } });
      setQaInput('');
      setQaAnonymous(false);
      fetchQA(selected.webinar_id);
    } catch { toast.error('Failed to submit question'); }
  };

  const handleUpvote = async (questionId) => {
    if (!selected) return;
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/qa/${questionId}/upvote`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchQA(selected.webinar_id);
    } catch { /* silent */ }
  };

  const handleQAAction = async (questionId, action, answerText = '') => {
    if (!selected) return;
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/qa/${questionId}/action`, { action, answer_text: answerText }, { headers: { Authorization: `Bearer ${token}` } });
      setAnsweringId(null);
      setAnswerInput('');
      fetchQA(selected.webinar_id);
    } catch { toast.error('Failed to perform action'); }
  };

  const sendReaction = async (emoji) => {
    if (!selected) return;
    // Show floating animation immediately (optimistic)
    const id = Date.now() + Math.random();
    const left = 10 + Math.random() * 60;
    setFloatingReactions(prev => [...prev, { id, emoji, left }]);
    setTimeout(() => setFloatingReactions(prev => prev.filter(r => r.id !== id)), 3000);

    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/reactions`, { emoji }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* silent — optimistic UI */ }
  };

  const fetchRecordings = async (webinarId) => {
    try {
      const res = await axios.get(`${API}/webinars/${webinarId}/recordings`, { headers: { Authorization: `Bearer ${token}` } });
      setRecordings(res.data);
    } catch { /* silent */ }
  };

  const handleAddRecording = async () => {
    if (!selected) return;

    // File upload mode
    if (uploadFile) {
      if (uploadFile.size > 200 * 1024 * 1024) {
        toast.error('File too large. Max 200MB.');
        return;
      }
      setIsUploading(true);
      setUploadProgress(0);
      try {
        const formData = new FormData();
        formData.append('file', uploadFile);
        const res = await axios.post(
          `${API}/webinars/${selected.webinar_id}/recordings/upload`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
              const pct = Math.round((e.loaded * 100) / (e.total || 1));
              setUploadProgress(pct);
            },
          }
        );
        // Update title/description if provided
        if (recordForm.title || recordForm.description) {
          const updates = {};
          if (recordForm.title) updates.title = recordForm.title;
          if (recordForm.description) updates.description = recordForm.description;
          if (recordForm.duration_minutes) updates.duration_minutes = recordForm.duration_minutes;
          // We patch via the recording doc directly
          await db_patch_recording(res.data.recording_id, updates);
        }
        toast.success('Recording uploaded successfully');
        setIsRecordOpen(false);
        setRecordForm({ title: '', description: '', url: '', duration_minutes: 0 });
        setUploadFile(null);
        setUploadProgress(0);
        fetchRecordings(selected.webinar_id);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Upload failed');
      } finally {
        setIsUploading(false);
      }
      return;
    }

    // External URL mode
    if (!recordForm.title) { toast.error('Title is required'); return; }
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/recordings`, recordForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Recording added');
      setIsRecordOpen(false);
      setRecordForm({ title: '', description: '', url: '', duration_minutes: 0 });
      fetchRecordings(selected.webinar_id);
    } catch { toast.error('Failed'); }
  };

  // Helper to patch recording metadata after upload
  const db_patch_recording = async (recordingId, updates) => {
    try {
      // Use the existing recording endpoint - we just update via API
      // For simplicity, we'll skip this if no dedicated PATCH endpoint exists
    } catch { /* silent */ }
  };

  const handleDeleteRecording = async (recId) => {
    try {
      await axios.delete(`${API}/recordings/${recId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Recording deleted');
      if (selected) fetchRecordings(selected.webinar_id);
    } catch { toast.error('Failed'); }
  };

  const handleTranscribe = async (recId) => {
    setTranscribing(prev => ({ ...prev, [recId]: true }));
    try {
      await axios.post(`${API}/recordings/${recId}/transcribe`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transcription started! You\'ll be notified when complete.');
      // Poll for status
      pollTranscriptionStatus(recId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start transcription');
      setTranscribing(prev => ({ ...prev, [recId]: false }));
    }
  };

  const pollTranscriptionStatus = (recId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/recordings/${recId}/transcript`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.transcription_status === 'completed' || res.data.transcription_status === 'failed') {
          clearInterval(interval);
          setTranscribing(prev => ({ ...prev, [recId]: false }));
          if (res.data.transcription_status === 'completed') {
            toast.success('Transcription complete!');
            setTranscriptData(res.data);
            // Refresh recordings to get updated metadata
            if (selected) fetchRecordings(selected.webinar_id);
          } else {
            toast.error('Transcription failed: ' + (res.data.error || 'Unknown error'));
          }
        }
      } catch {
        clearInterval(interval);
        setTranscribing(prev => ({ ...prev, [recId]: false }));
      }
    }, 5000);
    // Timeout after 5 minutes
    setTimeout(() => clearInterval(interval), 300000);
  };

  const fetchTranscript = async (recId) => {
    try {
      const res = await axios.get(`${API}/recordings/${recId}/transcript`, { headers: { Authorization: `Bearer ${token}` } });
      setTranscriptData(res.data);
      setShowTranscript(true);
      // If still processing, start polling
      if (res.data.transcription_status === 'processing') {
        setTranscribing(prev => ({ ...prev, [recId]: true }));
        pollTranscriptionStatus(recId);
      }
    } catch { toast.error('Failed to fetch transcript'); }
  };

  // ==================== ANNOTATIONS ====================

  const fetchAnnotations = async (recId) => {
    try {
      const res = await axios.get(`${API}/recordings/${recId}/annotations`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnotations(res.data);
    } catch { /* silent */ }
  };

  const handleAddAnnotation = async () => {
    if (!annotationInput.trim() || !playingRecording) return;
    const currentTime = videoRef.current?.currentTime || 0;
    try {
      await axios.post(`${API}/recordings/${playingRecording.recording_id}/annotations`, {
        content: annotationInput,
        timestamp_seconds: currentTime,
        type: annotationType,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAnnotationInput('');
      fetchAnnotations(playingRecording.recording_id);
      toast.success('Annotation added');
    } catch { toast.error('Failed to add annotation'); }
  };

  const handleDeleteAnnotation = async (annId) => {
    if (!playingRecording) return;
    try {
      await axios.delete(`${API}/recordings/${playingRecording.recording_id}/annotations/${annId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnotations(playingRecording.recording_id);
    } catch { toast.error('Failed to delete'); }
  };

  const handleReply = async (annId) => {
    const content = replyInputs[annId];
    if (!content?.trim() || !playingRecording) return;
    try {
      await axios.post(`${API}/recordings/${playingRecording.recording_id}/annotations/${annId}/replies`, { content }, { headers: { Authorization: `Bearer ${token}` } });
      setReplyInputs(prev => ({ ...prev, [annId]: '' }));
      fetchAnnotations(playingRecording.recording_id);
    } catch { toast.error('Failed to reply'); }
  };

  const handleReact = async (annId, emoji) => {
    if (!playingRecording) return;
    try {
      await axios.post(`${API}/recordings/${playingRecording.recording_id}/annotations/${annId}/react`, { emoji }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnotations(playingRecording.recording_id);
    } catch { /* silent */ }
  };

  const handleResolve = async (annId) => {
    if (!playingRecording) return;
    try {
      await axios.post(`${API}/recordings/${playingRecording.recording_id}/annotations/${annId}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnotations(playingRecording.recording_id);
    } catch { toast.error('Failed'); }
  };

  // ==================== CLIPS ====================

  const fetchClips = async (recId) => {
    try {
      const res = await axios.get(`${API}/recordings/${recId}/clips`, { headers: { Authorization: `Bearer ${token}` } });
      setClips(res.data);
    } catch { /* silent */ }
  };

  const openClipDialog = (startSec = 0, annId = '') => {
    const endSec = Math.min(startSec + 30, (videoRef.current?.duration || startSec + 30));
    setClipForm({ title: '', description: '', start_seconds: Math.floor(startSec), end_seconds: Math.floor(endSec), annotation_id: annId, tags: [] });
    setClipTagInput('');
    setIsClipOpen(true);
  };

  const handleCreateClip = async () => {
    if (!clipForm.title || !playingRecording) { toast.error('Title is required'); return; }
    try {
      await axios.post(`${API}/recordings/${playingRecording.recording_id}/clips`, clipForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Clip generation started! You\'ll be notified when ready.');
      setIsClipOpen(false);
      fetchClips(playingRecording.recording_id);
      // Poll for clip status
      setTimeout(() => fetchClips(playingRecording.recording_id), 5000);
      setTimeout(() => fetchClips(playingRecording.recording_id), 15000);
      setTimeout(() => fetchClips(playingRecording.recording_id), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create clip'); }
  };

  const handleDeleteClip = async (clipId) => {
    try {
      await axios.delete(`${API}/clips/${clipId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Clip deleted');
      if (playingRecording) fetchClips(playingRecording.recording_id);
    } catch { toast.error('Failed to delete'); }
  };

  const handleCreate = async () => {
    if (!form.title || !form.scheduled_at) { toast.error('Title and date are required'); return; }
    try {
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() };
      await axios.post(`${API}/webinars`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Webinar created');
      setIsCreateOpen(false);
      setForm({ title: '', description: '', scheduled_at: '', duration_minutes: 60, max_attendees: 100, waiting_room: false, recording_enabled: false, tags: [] });
      fetchWebinars();
    } catch { toast.error('Failed to create webinar'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this webinar? Attendees will be notified.')) return;
    try {
      await axios.delete(`${API}/webinars/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Webinar deleted');
      fetchWebinars();
    } catch { toast.error('Failed to delete'); }
  };

  const openView = async (w) => {
    try {
      const res = await axios.get(`${API}/webinars/${w.webinar_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data);
      setIsViewOpen(true);
    } catch { setSelected(w); setIsViewOpen(true); }
  };

  const handleJoinWebinar = async (webinar) => {
    try {
      const res = await axios.post(`${API}/webinars/${webinar.webinar_id}/join`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSelected({ ...webinar, ...res.data });
      // Fetch chat and polls
      const [chatRes, pollRes] = await Promise.all([
        axios.get(`${API}/webinars/${webinar.webinar_id}/chat`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/webinars/${webinar.webinar_id}/polls`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setChatMessages(chatRes.data);
      setPolls(pollRes.data);
      setIsViewOpen(false);
      setIsLiveOpen(true);
      startLivePolling(webinar.webinar_id);
      fetchBreakoutRooms(webinar.webinar_id);
      fetchQA(webinar.webinar_id);
    } catch { toast.error('Failed to join webinar'); }
  };

  const handleStartWebinar = async (id) => {
    try {
      await axios.post(`${API}/webinars/${id}/start`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Webinar started');
      fetchWebinars();
    } catch { toast.error('Failed to start'); }
  };

  const handleEndWebinar = async (id) => {
    try {
      await axios.post(`${API}/webinars/${id}/end`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Webinar ended');
      setIsLiveOpen(false);
      fetchWebinars();
    } catch { toast.error('Failed to end'); }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() || !selected) return;
    try {
      const res = await axios.post(`${API}/webinars/${selected.webinar_id}/chat`, { content: chatInput }, { headers: { Authorization: `Bearer ${token}` } });
      setChatMessages(prev => [...prev, res.data]);
      setChatInput('');
      setTimeout(() => chatRef.current?.scrollTo(0, chatRef.current.scrollHeight), 100);
    } catch { toast.error('Failed to send message'); }
  };

  const handleInvite = async () => {
    if (!inviteEmail || !selected) return;
    try {
      await axios.post(`${API}/webinars/${selected.webinar_id}/invite`, { email: inviteEmail, name: inviteName }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail(''); setInviteName(''); setIsInviteOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Join link copied');
  };

  const handleDownloadICS = (webinarId) => {
    window.open(`${API}/webinars/${webinarId}/calendar.ics`, '_blank');
  };

  const handleGoogleCalendar = async (webinarId) => {
    try {
      const res = await axios.get(`${API}/webinars/${webinarId}/calendar/google`, { headers: { Authorization: `Bearer ${token}` } });
      window.open(res.data.url, '_blank');
    } catch { toast.error('Failed to generate calendar link'); }
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const fetchAnalytics = async (webinarId) => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/webinars/${webinarId}/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(res.data);
    } catch { toast.error('Failed to fetch analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  const generateAIReport = async (webinarId) => {
    setGeneratingReport(true);
    try {
      const res = await axios.post(`${API}/webinars/${webinarId}/analytics/report`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(prev => ({ ...prev, has_ai_report: true, ai_report: res.data.content }));
      toast.success('AI report generated');
    } catch { toast.error('Failed to generate report'); }
    finally { setGeneratingReport(false); }
  };

  const openViewWithTab = async (w, tab = 'details') => {
    setViewTab(tab);
    try {
      const res = await axios.get(`${API}/webinars/${w.webinar_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data);
      setIsViewOpen(true);
      if (tab === 'analytics') fetchAnalytics(w.webinar_id);
      fetchRecordings(w.webinar_id);
    } catch { setSelected(w); setIsViewOpen(true); }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live': return 'bg-[#FF3B30] text-white';
      case 'ended': return 'bg-[#8E8E93] text-white';
      case 'cancelled': return 'bg-[#4B5563] text-white';
      default: return 'bg-[#007AFF] text-white';
    }
  };

  return (
    <div data-testid="webinars-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Webinars</h1>
          <p className="text-[#4B5563] text-sm">Schedule, manage, and join live webinars</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsTemplateListOpen(true)} data-testid="templates-btn">
            <Download className="w-4 h-4 mr-2" />Templates ({templates.length})
          </Button>
          <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsCreateOpen(true)} data-testid="create-webinar-btn">
            <Plus className="w-4 h-4 mr-2" />New Webinar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-[#E5E7EB] mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input placeholder="Search webinars..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="webinar-search" />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="webinar-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="ended">Ended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Webinars Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : webinars.length === 0 ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="text-center py-12 text-[#9CA3AF]">
            <Video className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
            <p>No webinars yet. Create your first webinar to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {webinars.map((w) => (
            <Card key={w.webinar_id} className="border-[#E5E7EB] hover:border-[#002FA7] transition-colors" data-testid={`webinar-card-${w.webinar_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <Badge className={getStatusBadge(w.status)}>{w.status === 'live' ? 'LIVE' : w.status}</Badge>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openViewWithTab(w, 'details')} data-testid={`view-webinar-${w.webinar_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[#FF3B30]" onClick={() => handleDelete(w.webinar_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <h3 className="font-semibold text-sm mb-1 line-clamp-2">{w.title}</h3>
                <div className="flex items-center gap-2 text-xs text-[#9CA3AF] mb-3">
                  <Calendar className="w-3 h-3" />
                  <span>{format(new Date(w.scheduled_at), 'MMM d, yyyy h:mm a')}</span>
                  {w.is_recurring && <Badge variant="secondary" className="text-[10px] flex items-center gap-0.5"><Repeat className="w-2.5 h-2.5" />{w.recurrence_rule}</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-[#9CA3AF] mb-3">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{w.duration_minutes}min</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{w.attendees?.length || 0}/{w.max_attendees}</span>
                </div>
                {w.tags?.length > 0 && (
                  <div className="flex gap-1 flex-wrap mb-3">
                    {w.tags.slice(0, 3).map((t, i) => <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>)}
                  </div>
                )}
                <div className="flex gap-2">
                  {w.status === 'scheduled' && w.host_id === user?.user_id && (
                    <Button size="sm" className="bg-[#34C759] hover:bg-[#2BAF4A] flex-1" onClick={() => handleStartWebinar(w.webinar_id)} data-testid={`start-webinar-${w.webinar_id}`}>
                      <Play className="w-3 h-3 mr-1" />Start
                    </Button>
                  )}
                  {(w.status === 'live' || w.status === 'scheduled') && (
                    <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A] flex-1" onClick={() => handleJoinWebinar(w)} data-testid={`join-webinar-${w.webinar_id}`}>
                      <Video className="w-3 h-3 mr-1" />Join
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleCopyLink(w.join_link)} data-testid={`copy-link-${w.webinar_id}`}>
                    <Link2 className="w-3 h-3" />
                  </Button>
                  {w.status === 'ended' && (
                    <Button size="sm" variant="outline" onClick={() => openViewWithTab(w, 'analytics')} data-testid={`analytics-webinar-${w.webinar_id}`}>
                      <BarChart3 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-webinar-dialog">
          <DialogHeader><DialogTitle>Create Webinar</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Webinar title" data-testid="webinar-title-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What is this webinar about?" rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date & Time *</Label><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="webinar-datetime-input" /></div>
              <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Max Attendees</Label><Input type="number" value={form.max_attendees} onChange={(e) => setForm({ ...form, max_attendees: parseInt(e.target.value) || 100 })} /></div>
              <div className="flex flex-col gap-3 pt-6">
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.waiting_room} onCheckedChange={(v) => setForm({ ...form, waiting_room: v })} /><Label className="text-sm">Waiting Room</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.recording_enabled} onCheckedChange={(v) => setForm({ ...form, recording_enabled: v })} /><Label className="text-sm">Recording</Label>
                </div>
              </div>
            </div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag..." onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
                <Button variant="outline" size="sm" onClick={addTag}>Add</Button>
              </div>
              {form.tags.length > 0 && <div className="flex gap-1 flex-wrap mt-2">{form.tags.map((t, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setForm({ ...form, tags: form.tags.filter((_, j) => j !== i) })}>{t} ×</Badge>)}</div>}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Checkbox checked={form.is_recurring} onCheckedChange={(v) => setForm({ ...form, is_recurring: v, recurrence_rule: v ? 'weekly' : '' })} data-testid="recurring-toggle" />
                <Label className="text-sm flex items-center gap-1"><Repeat className="w-3.5 h-3.5" />Recurring Schedule</Label>
              </div>
              {form.is_recurring && (
                <Select value={form.recurrence_rule} onValueChange={(v) => setForm({ ...form, recurrence_rule: v })}>
                  <SelectTrigger data-testid="recurrence-rule-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreate} data-testid="save-webinar-btn">Create Webinar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Detail Dialog */}
      <Dialog open={isViewOpen} onOpenChange={(v) => { setIsViewOpen(v); if (!v) { setAnalytics(null); setViewTab('details'); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-webinar-dialog">
          <DialogHeader><DialogTitle>{selected?.title}</DialogTitle></DialogHeader>
          {selected && (
            <Tabs value={viewTab} onValueChange={(v) => { setViewTab(v); if (v === 'analytics' && !analytics) fetchAnalytics(selected.webinar_id); }}>
              <TabsList className="mb-4">
                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
                <TabsTrigger value="recordings" data-testid="tab-recordings">Recordings ({recordings.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Badge className={getStatusBadge(selected.status)}>{selected.status === 'live' ? 'LIVE' : selected.status}</Badge>
                    <span className="text-sm text-[#9CA3AF]">{format(new Date(selected.scheduled_at), 'MMMM d, yyyy at h:mm a')}</span>
                    <span className="text-sm text-[#9CA3AF]">{selected.duration_minutes} min</span>
                  </div>
                  {selected.description && <p className="text-sm text-[#4B5563]">{selected.description}</p>}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-[#9CA3AF]">Host</span><p className="font-medium">{selected.host_name}</p></div>
                    <div><span className="text-[#9CA3AF]">Attendees</span><p className="font-medium">{selected.attendees?.length || 0} / {selected.max_attendees}</p></div>
                  </div>
                  <div className="p-3 bg-[#F3F4F6] rounded-lg">
                    <Label className="text-xs text-[#9CA3AF] uppercase tracking-wider">Join Link</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input value={selected.join_link} readOnly className="text-sm bg-white" />
                      <Button size="sm" variant="outline" onClick={() => handleCopyLink(selected.join_link)} data-testid="copy-join-link"><Link2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleDownloadICS(selected.webinar_id)} data-testid="download-ics-btn"><Download className="w-3.5 h-3.5 mr-1.5" />Download .ics</Button>
                    <Button size="sm" variant="outline" onClick={() => handleGoogleCalendar(selected.webinar_id)} data-testid="google-cal-btn"><Calendar className="w-3.5 h-3.5 mr-1.5" />Google Calendar</Button>
                    <Button size="sm" variant="outline" onClick={() => setIsInviteOpen(true)} data-testid="invite-btn"><Mail className="w-3.5 h-3.5 mr-1.5" />Invite</Button>
                  </div>
                  {selected.attendees?.length > 0 && (
                    <div>
                      <Label className="text-xs text-[#9CA3AF] uppercase tracking-wider">Attendees ({selected.attendees.length})</Label>
                      <div className="mt-2 space-y-1">
                        {selected.attendees.map((a, i) => (
                          <div key={i} className="flex items-center justify-between text-sm p-2 bg-[#F9FAFB] rounded">
                            <span>{a.user_name || a.name || a.email}</span>
                            <Badge variant="secondary" className="text-[10px]">{a.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
                    {(selected.status === 'live' || selected.status === 'scheduled') && (
                      <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => handleJoinWebinar(selected)} data-testid="join-from-view-btn"><Video className="w-4 h-4 mr-2" />Join Webinar</Button>
                    )}
                    {selected.status === 'scheduled' && selected.host_id === user?.user_id && (
                      <Button className="bg-[#34C759] hover:bg-[#2BAF4A]" onClick={() => handleStartWebinar(selected.webinar_id)}><Play className="w-4 h-4 mr-2" />Start Now</Button>
                    )}
                    <Button variant="outline" onClick={() => handleSaveAsTemplate(selected.webinar_id)} data-testid="save-as-template-btn">
                      <Download className="w-4 h-4 mr-2" />Save as Template
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="analytics">
                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
                ) : !analytics ? (
                  <div className="text-center py-12 text-[#9CA3AF]"><BarChart3 className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" /><p className="text-sm">No analytics available yet.</p></div>
                ) : (
                  <div className="space-y-5" data-testid="analytics-content">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Attendance Rate', value: `${analytics.attendance?.attendance_rate || 0}%`, icon: Users, color: 'text-[#002FA7]' },
                        { label: 'Chat Messages', value: analytics.chat?.total_messages || 0, icon: MessageSquare, color: 'text-[#34C759]' },
                        { label: 'Polls', value: analytics.polls?.total_polls || 0, icon: PieChart, color: 'text-[#FF9500]' },
                        { label: 'Avg Engagement', value: `${analytics.engagement?.avg_score || 0}%`, icon: TrendingUp, color: 'text-[#AF52DE]' },
                      ].map((s, i) => {
                        const Icon = s.icon;
                        return (
                          <div key={i} className="p-3 bg-[#F3F4F6] rounded-lg text-center">
                            <Icon className={`w-5 h-5 mx-auto mb-1 ${s.color}`} />
                            <p className="text-lg font-bold">{s.value}</p>
                            <p className="text-xs text-[#9CA3AF]">{s.label}</p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Detail Metrics */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 border border-[#E5E7EB] rounded-lg">
                        <h4 className="font-medium mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-[#002FA7]" />Attendance</h4>
                        <div className="space-y-1 text-[#4B5563]">
                          <div className="flex justify-between"><span>Registered</span><span className="font-medium">{analytics.attendance?.total_registered}</span></div>
                          <div className="flex justify-between"><span>Joined</span><span className="font-medium">{analytics.attendance?.total_joined}</span></div>
                          <div className="flex justify-between"><span>Duration</span><span className="font-medium">{analytics.actual_duration_min} min</span></div>
                        </div>
                      </div>
                      <div className="p-3 border border-[#E5E7EB] rounded-lg">
                        <h4 className="font-medium mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#34C759]" />Chat Activity</h4>
                        <div className="space-y-1 text-[#4B5563]">
                          <div className="flex justify-between"><span>Messages</span><span className="font-medium">{analytics.chat?.total_messages}</span></div>
                          <div className="flex justify-between"><span>Per Minute</span><span className="font-medium">{analytics.chat?.messages_per_minute}</span></div>
                          <div className="flex justify-between"><span>Active Chatters</span><span className="font-medium">{analytics.chat?.active_chatters}</span></div>
                        </div>
                      </div>
                    </div>

                    {/* Attendee Engagement Table */}
                    {analytics.engagement?.attendee_details?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2 text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#AF52DE]" />Attendee Engagement</h4>
                        <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-[#F3F4F6]">
                              <tr>
                                <th className="text-left p-2 font-medium">Attendee</th>
                                <th className="text-center p-2 font-medium">Time</th>
                                <th className="text-center p-2 font-medium">Chat</th>
                                <th className="text-center p-2 font-medium">Polls</th>
                                <th className="text-center p-2 font-medium">Score</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.engagement.attendee_details.map((a, i) => (
                                <tr key={i} className="border-t border-[#E5E7EB]">
                                  <td className="p-2">{a.name}</td>
                                  <td className="text-center p-2">{a.time_spent_min}m</td>
                                  <td className="text-center p-2">{a.chat_messages}</td>
                                  <td className="text-center p-2">{a.polls_participated}</td>
                                  <td className="text-center p-2">
                                    <div className="flex items-center justify-center gap-1">
                                      <div className="w-12 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${a.engagement_score >= 70 ? 'bg-[#34C759]' : a.engagement_score >= 40 ? 'bg-[#FF9500]' : 'bg-[#FF3B30]'}`} style={{ width: `${a.engagement_score}%` }} />
                                      </div>
                                      <span className="text-xs font-medium">{a.engagement_score}%</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* AI Report */}
                    <div className="border-t border-[#E5E7EB] pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#002FA7]" />AI-Powered Report</h4>
                        <Button size="sm" onClick={() => generateAIReport(selected.webinar_id)} disabled={generatingReport} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="generate-report-btn">
                          {generatingReport ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />{analytics.has_ai_report ? 'Regenerate' : 'Generate Report'}</>}
                        </Button>
                      </div>
                      {analytics.ai_report ? (
                        <div className="prose prose-sm max-w-none text-sm text-[#4B5563] p-4 bg-[#F9FAFB] rounded-lg [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-[#0A0A0A] [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-[#0A0A0A] [&_strong]:text-[#0A0A0A]" data-testid="ai-report-content">
                          {analytics.ai_report.split('\n').map((line, i) => {
                            if (!line.trim()) return <br key={i} />;
                            if (line.startsWith('### ')) return <h3 key={i}>{line.slice(4)}</h3>;
                            if (line.startsWith('## ')) return <h2 key={i}>{line.slice(3)}</h2>;
                            if (line.startsWith('- ')) return <div key={i} className="flex gap-2 ml-2"><span className="text-[#002FA7]">-</span><span>{line.slice(2)}</span></div>;
                            if (line.match(/^\d+\.\s/)) return <div key={i} className="flex gap-2 ml-2"><span className="text-[#002FA7] font-medium">{line.match(/^\d+/)[0]}.</span><span>{line.replace(/^\d+\.\s/, '')}</span></div>;
                            return <p key={i}>{line}</p>;
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-[#9CA3AF] p-4 bg-[#F9FAFB] rounded-lg text-center">Click "Generate Report" for AI-powered insights about this webinar.</p>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Recordings Tab */}
              <TabsContent value="recordings">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-sm">Webinar Recordings</h4>
                    {selected?.host_id === user?.user_id && (
                      <Button size="sm" onClick={() => { setIsRecordOpen(true); setUploadFile(null); setUploadProgress(0); }} data-testid="add-recording-btn">
                        <Plus className="w-3 h-3 mr-1" />Add Recording
                      </Button>
                    )}
                  </div>

                  {/* Video Player with Chapter Markers */}
                  {playingRecording && (
                    <div className="border border-[#002FA7] rounded-lg overflow-hidden bg-black" data-testid="video-player-container">
                      <div className="flex items-center justify-between p-2 bg-[#0A0A0A]">
                        <span className="text-white text-xs font-medium truncate flex-1">{playingRecording.title}</span>
                        <div className="flex items-center gap-1">
                          {playingRecording.transcription_status === 'completed' && !showTranscript && (
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-white/70 hover:text-white" onClick={() => fetchTranscript(playingRecording.recording_id)}>
                              <FileText className="w-3 h-3 mr-1" />Chapters
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            className={`h-6 text-[10px] ${showAnnotations ? 'text-[#FF9500]' : 'text-white/70 hover:text-white'}`}
                            onClick={() => {
                              if (!showAnnotations) fetchAnnotations(playingRecording.recording_id);
                              setShowAnnotations(!showAnnotations);
                            }}
                            data-testid="toggle-annotations-btn"
                          >
                            <MessageCircle className="w-3 h-3 mr-1" />
                            {annotations.length > 0 ? `${annotations.length}` : 'Annotate'}
                          </Button>
                          {playingRecording.storage_type === 'cloud' && (
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-white/70 hover:text-white" onClick={() => openClipDialog(videoRef.current?.currentTime || 0)} data-testid="create-clip-btn">
                              <Scissors className="w-3 h-3 mr-1" />Clip
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:text-[#FF3B30]" onClick={() => { setPlayingRecording(null); setShowTranscript(false); setTranscriptData(null); setShowAnnotations(false); setAnnotations([]); setClips([]); }}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      {playingRecording.storage_type === 'cloud' && playingRecording.storage_path ? (
                        <video
                          ref={videoRef}
                          controls
                          autoPlay
                          className="w-full max-h-[400px]"
                          src={`${API}/recordings/${playingRecording.recording_id}/stream?t=${token}`}
                          data-testid="video-player"
                        >
                          Your browser does not support video playback.
                        </video>
                      ) : playingRecording.url ? (
                        <video ref={videoRef} controls autoPlay className="w-full max-h-[400px]" src={playingRecording.url} data-testid="video-player">
                          Your browser does not support video playback.
                        </video>
                      ) : (
                        <div className="p-8 text-center text-[#9CA3AF]">
                          <CloudOff className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">No playable file available</p>
                        </div>
                      )}
                      {/* Chapter Timeline Bar */}
                      {showTranscript && transcriptData?.chapters?.length > 0 && (
                        <ChapterTimeline
                          chapters={transcriptData.chapters}
                          totalSeconds={transcriptData.duration_seconds || 0}
                          onSeek={(seconds) => {
                            if (videoRef.current) {
                              videoRef.current.currentTime = seconds;
                              videoRef.current.play();
                            }
                          }}
                        />
                      )}
                      {/* Annotations Panel */}
                      {showAnnotations && (
                        <AnnotationsPanel
                          annotations={annotations}
                          currentUserId={user?.user_id}
                          onAdd={handleAddAnnotation}
                          onDelete={handleDeleteAnnotation}
                          onReply={handleReply}
                          onReact={handleReact}
                          onResolve={handleResolve}
                          onClip={(startSec, annId) => openClipDialog(startSec, annId)}
                          onSeek={(seconds) => { if (videoRef.current) { videoRef.current.currentTime = seconds; videoRef.current.play(); } }}
                          annotationInput={annotationInput}
                          setAnnotationInput={setAnnotationInput}
                          annotationType={annotationType}
                          setAnnotationType={setAnnotationType}
                          replyInputs={replyInputs}
                          setReplyInputs={setReplyInputs}
                          videoRef={videoRef}
                        />
                      )}
                    </div>
                  )}

                  {recordings.length === 0 ? (
                    <div className="text-center py-8 text-[#9CA3AF]"><Film className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" /><p className="text-sm">No recordings yet</p></div>
                  ) : (
                    recordings.map((rec) => (
                      <div key={rec.recording_id} className={`p-3 border rounded-lg transition-colors ${playingRecording?.recording_id === rec.recording_id ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-[#E5E7EB]'}`} data-testid={`recording-${rec.recording_id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h5 className="font-medium text-sm truncate">{rec.title}</h5>
                              {rec.storage_type === 'cloud' && <Badge variant="secondary" className="text-[9px] flex items-center gap-0.5"><Cloud className="w-2.5 h-2.5" />Cloud</Badge>}
                              {rec.transcription_status === 'completed' && <Badge variant="secondary" className="text-[9px] bg-[#34C759]/10 text-[#34C759] flex items-center gap-0.5"><FileText className="w-2.5 h-2.5" />Transcribed</Badge>}
                              {rec.transcription_status === 'processing' && <Badge variant="secondary" className="text-[9px] bg-[#FF9500]/10 text-[#FF9500] flex items-center gap-0.5"><Loader2 className="w-2.5 h-2.5 animate-spin" />Transcribing</Badge>}
                            </div>
                            {rec.description && <p className="text-xs text-[#4B5563] mt-0.5">{rec.description}</p>}
                            {rec.transcript_summary && <p className="text-xs text-[#4B5563] mt-1 italic line-clamp-2">{rec.transcript_summary}</p>}
                            <div className="flex items-center gap-3 mt-1 text-xs text-[#9CA3AF]">
                              {rec.duration_minutes > 0 && <span>{rec.duration_minutes} min</span>}
                              <span>{rec.views} views</span>
                              <span>{rec.recorded_by_name}</span>
                              {rec.file_size > 0 && <span>{(rec.file_size / (1024 * 1024)).toFixed(1)} MB</span>}
                            </div>
                            {rec.transcript_key_topics?.length > 0 && (
                              <div className="flex gap-1 flex-wrap mt-1.5">
                                {rec.transcript_key_topics.slice(0, 4).map((t, i) => <Badge key={i} variant="secondary" className="text-[9px] bg-[#002FA7]/5 text-[#002FA7]">{t}</Badge>)}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 ml-2 flex-shrink-0">
                            {(rec.storage_path || rec.url) && (
                              <Button size="sm" variant="outline" onClick={() => { setPlayingRecording(rec); fetchAnnotations(rec.recording_id); fetchClips(rec.recording_id); }} data-testid={`play-recording-${rec.recording_id}`}>
                                <Play className="w-3 h-3 mr-1" />Play
                              </Button>
                            )}
                            {rec.storage_type === 'cloud' && rec.storage_path && !rec.transcription_status && (
                              <Button size="sm" variant="outline" onClick={() => handleTranscribe(rec.recording_id)} disabled={transcribing[rec.recording_id]} data-testid={`transcribe-recording-${rec.recording_id}`}>
                                {transcribing[rec.recording_id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mic className="w-3 h-3 mr-1" />}
                                Transcribe
                              </Button>
                            )}
                            {rec.transcription_status === 'processing' && (
                              <Button size="sm" variant="outline" disabled>
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />Processing
                              </Button>
                            )}
                            {rec.transcription_status === 'completed' && (
                              <Button size="sm" variant="outline" onClick={() => fetchTranscript(rec.recording_id)} data-testid={`view-transcript-${rec.recording_id}`}>
                                <FileText className="w-3 h-3 mr-1" />Transcript
                              </Button>
                            )}
                            {rec.transcription_status === 'failed' && (
                              <Button size="sm" variant="outline" className="text-[#FF3B30]" onClick={() => handleTranscribe(rec.recording_id)} data-testid={`retry-transcribe-${rec.recording_id}`}>
                                <Mic className="w-3 h-3 mr-1" />Retry
                              </Button>
                            )}
                            {rec.url && !rec.storage_path && (
                              <a href={rec.url} target="_blank" rel="noopener noreferrer">
                                <Button size="sm" variant="ghost"><ExternalLink className="w-3 h-3" /></Button>
                              </a>
                            )}
                            {selected?.host_id === user?.user_id && (
                              <Button size="sm" variant="ghost" className="text-[#FF3B30]" onClick={() => handleDeleteRecording(rec.recording_id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {/* Transcript Viewer */}
                  {showTranscript && transcriptData && (
                    <TranscriptViewer
                      data={transcriptData}
                      onClose={() => { setShowTranscript(false); setTranscriptData(null); }}
                      onSeek={(seconds) => {
                        if (videoRef.current) {
                          videoRef.current.currentTime = seconds;
                          videoRef.current.play();
                        }
                      }}
                    />
                  )}

                  {/* Knowledge Clips */}
                  {playingRecording && clips.length > 0 && (
                    <div className="border border-[#FF9500]/20 rounded-lg bg-[#FF9500]/5 overflow-hidden" data-testid="clips-section">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-[#FF9500]/10">
                        <div className="flex items-center gap-2">
                          <Scissors className="w-3.5 h-3.5 text-[#FF9500]" />
                          <span className="text-xs font-semibold">Knowledge Clips ({clips.length})</span>
                        </div>
                      </div>
                      <div className="p-2 space-y-1.5 max-h-[200px] overflow-y-auto">
                        {clips.map((clip) => (
                          <div key={clip.clip_id} className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${playingClip?.clip_id === clip.clip_id ? 'border-[#FF9500] bg-white' : 'border-transparent hover:bg-white/50'}`} data-testid={`clip-${clip.clip_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-mono text-[#FF9500] font-medium">{clip.start_display}–{clip.end_display}</span>
                                <h6 className="text-xs font-medium truncate">{clip.title}</h6>
                                <Badge variant="secondary" className={`text-[8px] ${clip.status === 'ready' ? 'bg-[#34C759]/10 text-[#34C759]' : clip.status === 'processing' || clip.status === 'queued' ? 'bg-[#FF9500]/10 text-[#FF9500]' : 'bg-[#FF3B30]/10 text-[#FF3B30]'}`}>
                                  {clip.status === 'ready' ? 'Ready' : clip.status === 'processing' || clip.status === 'queued' ? 'Generating...' : 'Failed'}
                                </Badge>
                              </div>
                              {clip.description && <p className="text-[10px] text-[#9CA3AF] truncate">{clip.description}</p>}
                              <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF] mt-0.5">
                                <span>{clip.actual_duration}s</span>
                                {clip.views > 0 && <span>{clip.views} views</span>}
                                {clip.file_size > 0 && <span>{(clip.file_size / (1024 * 1024)).toFixed(1)} MB</span>}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {clip.status === 'ready' && (
                                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setPlayingClip(playingClip?.clip_id === clip.clip_id ? null : clip)} data-testid={`play-clip-${clip.clip_id}`}>
                                  <Play className="w-2.5 h-2.5 mr-0.5" />{playingClip?.clip_id === clip.clip_id ? 'Stop' : 'Play'}
                                </Button>
                              )}
                              {clip.created_by === user?.user_id && (
                                <Button size="sm" variant="ghost" className="h-6 text-[#FF3B30]" onClick={() => handleDeleteClip(clip.clip_id)}>
                                  <Trash2 className="w-2.5 h-2.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Clip Player */}
                      {playingClip && playingClip.status === 'ready' && (
                        <div className="border-t border-[#FF9500]/10 bg-black">
                          <div className="flex items-center justify-between p-1.5 bg-[#0A0A0A]">
                            <span className="text-white text-[10px] truncate flex-1">{playingClip.title}</span>
                            <Button variant="ghost" size="icon" className="h-5 w-5 text-white hover:text-[#FF3B30]" onClick={() => setPlayingClip(null)}><X className="w-3 h-3" /></Button>
                          </div>
                          <video controls autoPlay className="w-full max-h-[200px]" src={`${API}/clips/${playingClip.clip_id}/stream?t=${token}`} data-testid="clip-player" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Live Webinar Dialog (with embedded Jitsi) */}
      <Dialog open={isLiveOpen} onOpenChange={(v) => { if (!v) { setIsLiveOpen(false); stopLivePolling(); fetchWebinars(); } }}>
        <DialogContent className="max-w-6xl h-[90vh] p-0" data-testid="live-webinar-dialog">
          {selected && (
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-3 border-b border-[#E5E7EB] bg-white">
                <div className="flex items-center gap-3">
                  <Badge className="bg-[#FF3B30] text-white animate-pulse">LIVE</Badge>
                  <h3 className="font-semibold text-sm">{selected.title}</h3>
                </div>
                {/* Live Stats */}
                {liveStats && (
                  <div className="flex items-center gap-4 text-xs" data-testid="live-stats-bar">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3 text-[#34C759]" /><strong>{liveStats.active_attendees}</strong> active</span>
                    <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3 text-[#007AFF]" /><strong>{liveStats.chat_total}</strong> msgs</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-[#FF9500]" /><strong>{liveStats.elapsed_minutes}</strong>m</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {selected.host_id === user?.user_id && (
                    <Button size="sm" variant="outline" className="text-[#FF3B30] border-[#FF3B30]" onClick={() => handleEndWebinar(selected.webinar_id)} data-testid="end-webinar-btn">
                      <Square className="w-3 h-3 mr-1" />End
                    </Button>
                  )}
                </div>
              </div>

              {/* Main content */}
              <div className="flex-1 flex overflow-hidden">
                {/* Video area */}
                <div className="flex-1 bg-[#0A0A0A] relative">
                  <iframe
                    src={`https://${selected.room_name ? 'meet.jit.si/' + selected.room_name : 'meet.jit.si/KStreak-Webinar'}#config.prejoinConfig.enabled=false&config.startWithAudioMuted=true`}
                    allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
                    className="w-full h-full border-0"
                    data-testid="jitsi-iframe"
                  />

                  {/* Floating Reactions */}
                  <div className="absolute bottom-16 left-0 right-0 pointer-events-none overflow-hidden h-64">
                    {floatingReactions.map((r) => (
                      <span key={r.id} className="absolute text-3xl animate-float-up" style={{ left: `${r.left}%`, bottom: 0 }}>{r.emoji}</span>
                    ))}
                  </div>

                  {/* Reaction Bar */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5" data-testid="reaction-bar">
                    {['👏', '🔥', '❤️', '😮', '😂', '👍'].map((emoji) => (
                      <button key={emoji} onClick={() => sendReaction(emoji)} className="text-xl hover:scale-125 transition-transform active:scale-90 cursor-pointer" data-testid={`reaction-${emoji}`}>
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sidebar: Chat / Q&A / Breakout */}
                <div className="w-80 flex flex-col border-l border-[#E5E7EB] bg-white">
                  {/* Tab switcher */}
                  <div className="flex border-b border-[#E5E7EB]">
                    {[
                      { key: 'chat', label: 'Chat', icon: MessageSquare },
                      { key: 'qa', label: 'Q&A', icon: () => <span className="text-[10px] font-bold">Q&A</span> },
                      { key: 'breakout', label: 'Rooms', icon: SplitSquareHorizontal },
                    ].map(({ key, label, icon: Icon }) => (
                      <button key={key} onClick={() => setSidebarTab(key)}
                        className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors ${sidebarTab === key ? 'text-[#002FA7] border-b-2 border-[#002FA7]' : 'text-[#9CA3AF] hover:text-[#4B5563]'}`}
                        data-testid={`sidebar-tab-${key}`}>
                        <Icon className="w-3.5 h-3.5" />{label}
                      </button>
                    ))}
                  </div>

                  {/* Chat Panel */}
                  {sidebarTab === 'chat' && (
                    <>
                      <div ref={chatRef} className="flex-1 overflow-y-auto p-3 space-y-2">
                        {chatMessages.length === 0 && <p className="text-xs text-[#9CA3AF] text-center py-4">No messages yet</p>}
                        {chatMessages.map((m) => (
                          <div key={m.chat_id} className={`text-xs ${m.user_id === user?.user_id ? 'text-right' : ''}`}>
                            <span className="font-medium text-[#002FA7]">{m.user_name}</span>
                            <p className={`p-2 rounded-lg mt-0.5 inline-block max-w-[200px] text-left ${m.user_id === user?.user_id ? 'bg-[#002FA7] text-white' : 'bg-[#F3F4F6]'}`}>{m.content}</p>
                          </div>
                        ))}
                      </div>
                      <div className="p-2 border-t border-[#E5E7EB] flex gap-1">
                        <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Message..." className="text-sm" onKeyDown={(e) => e.key === 'Enter' && handleSendChat()} data-testid="chat-input" />
                        <Button size="sm" variant="ghost" onClick={handleSendChat}><Send className="w-3.5 h-3.5" /></Button>
                      </div>
                    </>
                  )}

                  {/* Q&A Panel */}
                  {sidebarTab === 'qa' && (
                    <>
                      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="qa-panel">
                        {/* Pinned */}
                        {qaQuestions.pinned.map((q) => (
                          <QACard key={q.question_id} q={q} isPinned isHost={selected?.host_id === user?.user_id} userId={user?.user_id}
                            onUpvote={handleUpvote} onAction={handleQAAction} answeringId={answeringId} setAnsweringId={setAnsweringId}
                            answerInput={answerInput} setAnswerInput={setAnswerInput} />
                        ))}
                        {/* Active */}
                        {qaQuestions.active.map((q) => (
                          <QACard key={q.question_id} q={q} isHost={selected?.host_id === user?.user_id} userId={user?.user_id}
                            onUpvote={handleUpvote} onAction={handleQAAction} answeringId={answeringId} setAnsweringId={setAnsweringId}
                            answerInput={answerInput} setAnswerInput={setAnswerInput} />
                        ))}
                        {/* Answered */}
                        {qaQuestions.answered.length > 0 && (
                          <div className="pt-2 border-t border-[#E5E7EB]">
                            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Answered</p>
                            {qaQuestions.answered.map((q) => (
                              <QACard key={q.question_id} q={q} isAnswered isHost={selected?.host_id === user?.user_id} userId={user?.user_id}
                                onUpvote={handleUpvote} onAction={handleQAAction} answeringId={answeringId} setAnsweringId={setAnsweringId}
                                answerInput={answerInput} setAnswerInput={setAnswerInput} />
                            ))}
                          </div>
                        )}
                        {qaQuestions.total === 0 && <p className="text-xs text-[#9CA3AF] text-center py-4">No questions yet. Be the first to ask!</p>}
                      </div>
                      <div className="p-2 border-t border-[#E5E7EB]">
                        <div className="flex items-center gap-1 mb-1">
                          <Checkbox checked={qaAnonymous} onCheckedChange={setQaAnonymous} className="h-3 w-3" />
                          <span className="text-[10px] text-[#9CA3AF]">Anonymous</span>
                        </div>
                        <div className="flex gap-1">
                          <Input value={qaInput} onChange={(e) => setQaInput(e.target.value)} placeholder="Ask a question..." className="text-sm" onKeyDown={(e) => e.key === 'Enter' && handleSubmitQuestion()} data-testid="qa-input" />
                          <Button size="sm" variant="ghost" onClick={handleSubmitQuestion}><Send className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Breakout Rooms Panel */}
                  {sidebarTab === 'breakout' && (
                    <>
                      <div className="p-2 border-b border-[#E5E7EB] flex items-center justify-between">
                        {selected?.host_id === user?.user_id && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setIsBreakoutOpen(true)} data-testid="create-breakout-btn">
                            <Plus className="w-3 h-3 mr-1" />New Room
                          </Button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {breakoutRooms.length === 0 ? (
                          <p className="text-xs text-[#9CA3AF] text-center py-4">No breakout rooms</p>
                        ) : (
                          <>
                            {breakoutRooms.map((br) => (
                              <div key={br.room_id} className={`p-2 rounded-lg text-xs ${br.status === 'active' ? 'bg-[#34C759]/10 border border-[#34C759]/30' : 'bg-[#F3F4F6]'}`} data-testid={`breakout-room-${br.room_id}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{br.name}</span>
                                  <Badge className={br.status === 'active' ? 'bg-[#34C759] text-white text-[9px]' : 'bg-[#8E8E93] text-white text-[9px]'}>{br.status}</Badge>
                                </div>
                                <div className="text-[#9CA3AF] mb-1">{br.assigned_users?.length || 0} users · {br.duration_minutes}min</div>
                                {br.status === 'active' && (
                                  <div className="flex gap-1">
                                    <a href={br.join_link} target="_blank" rel="noopener noreferrer" className="flex-1">
                                      <Button size="sm" variant="outline" className="w-full h-6 text-[10px]"><ExternalLink className="w-2.5 h-2.5 mr-1" />Join</Button>
                                    </a>
                                    {selected?.host_id === user?.user_id && (
                                      <Button size="sm" variant="outline" className="h-6 text-[10px] text-[#FF3B30]" onClick={() => handleEndBreakout(br.room_id)} data-testid={`end-breakout-${br.room_id}`}>
                                        <Square className="w-2.5 h-2.5" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                            {selected?.host_id === user?.user_id && breakoutRooms.some(r => r.status === 'active') && (
                              <Button size="sm" variant="outline" className="w-full text-xs text-[#FF3B30] border-[#FF3B30]" onClick={handleEndAllBreakouts} data-testid="end-all-breakouts-btn">
                                <ArrowLeftRight className="w-3 h-3 mr-1" />End All & Reassemble
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Recording Dialog */}
      <Dialog open={isRecordOpen} onOpenChange={(v) => { if (!isUploading) { setIsRecordOpen(v); if (!v) { setUploadFile(null); setUploadProgress(0); } } }}>
        <DialogContent data-testid="add-recording-dialog">
          <DialogHeader><DialogTitle>Add Recording</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Upload/URL Tab Toggle */}
            <div className="flex bg-[#F3F4F6] rounded-lg p-1">
              <button
                onClick={() => setUploadFile(null)}
                className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-colors ${!uploadFile ? 'bg-white text-[#002FA7] shadow-sm' : 'text-[#9CA3AF]'}`}
                data-testid="recording-tab-url"
                disabled={isUploading}
              >
                <Link2 className="w-3 h-3 inline mr-1" />External URL
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-colors ${uploadFile ? 'bg-white text-[#002FA7] shadow-sm' : 'text-[#9CA3AF]'}`}
                data-testid="recording-tab-upload"
                disabled={isUploading}
              >
                <Upload className="w-3 h-3 inline mr-1" />Upload File
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,audio/*,.mp4,.webm,.mov,.avi,.mkv,.mp3,.wav"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setUploadFile(f);
                  if (!recordForm.title) setRecordForm(prev => ({ ...prev, title: f.name.replace(/\.[^/.]+$/, '') }));
                }
              }}
              data-testid="recording-file-input"
            />

            {/* File Upload Area */}
            {uploadFile ? (
              <div className="border-2 border-dashed border-[#002FA7]/30 rounded-lg p-4 bg-[#002FA7]/5">
                <div className="flex items-center gap-3">
                  <Film className="w-8 h-8 text-[#002FA7]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{uploadFile.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{(uploadFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                  </div>
                  {!isUploading && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setUploadFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                {isUploading && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[#002FA7] font-medium">Uploading...</span>
                      <span className="text-[#4B5563]">{uploadProgress}%</span>
                    </div>
                    <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                      <div className="h-full bg-[#002FA7] rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} data-testid="upload-progress-bar" />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div
                  className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-6 text-center cursor-pointer hover:border-[#002FA7]/30 hover:bg-[#F9FAFB] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="recording-drop-zone"
                >
                  <Upload className="w-8 h-8 mx-auto mb-2 text-[#9CA3AF]" />
                  <p className="text-sm text-[#4B5563]">Click to select a video file</p>
                  <p className="text-xs text-[#9CA3AF] mt-1">MP4, WebM, MOV, AVI up to 200MB</p>
                </div>
                <div className="relative flex items-center">
                  <div className="flex-1 border-t border-[#E5E7EB]" />
                  <span className="px-3 text-xs text-[#9CA3AF]">or paste URL</span>
                  <div className="flex-1 border-t border-[#E5E7EB]" />
                </div>
              </>
            )}

            <div><Label>Title</Label><Input value={recordForm.title} onChange={e => setRecordForm({...recordForm, title: e.target.value})} placeholder="Recording title" data-testid="recording-title-input" disabled={isUploading} /></div>
            <div><Label>Description</Label><Textarea value={recordForm.description} onChange={e => setRecordForm({...recordForm, description: e.target.value})} rows={2} disabled={isUploading} /></div>
            {!uploadFile && (
              <div><Label>Recording URL</Label><Input value={recordForm.url} onChange={e => setRecordForm({...recordForm, url: e.target.value})} placeholder="https://..." data-testid="recording-url-input" /></div>
            )}
            <div><Label>Duration (minutes)</Label><Input type="number" value={recordForm.duration_minutes} onChange={e => setRecordForm({...recordForm, duration_minutes: parseInt(e.target.value) || 0})} disabled={isUploading} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { if (!isUploading) { setIsRecordOpen(false); setUploadFile(null); } }} disabled={isUploading}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleAddRecording} disabled={isUploading} data-testid="save-recording-btn">
              {isUploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Uploading {uploadProgress}%</> : uploadFile ? <><Upload className="w-3.5 h-3.5 mr-1.5" />Upload Recording</> : 'Save Recording'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Clip Dialog */}
      <Dialog open={isClipOpen} onOpenChange={setIsClipOpen}>
        <DialogContent data-testid="create-clip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-[#FF9500]" />Create Knowledge Clip
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={clipForm.title} onChange={e => setClipForm({...clipForm, title: e.target.value})} placeholder="Clip title..." data-testid="clip-title-input" /></div>
            <div><Label>Description</Label><Textarea value={clipForm.description} onChange={e => setClipForm({...clipForm, description: e.target.value})} rows={2} placeholder="What makes this moment important?" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time (seconds)</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} step={1} value={clipForm.start_seconds} onChange={e => setClipForm({...clipForm, start_seconds: Math.max(0, parseInt(e.target.value) || 0)})} data-testid="clip-start-input" />
                  <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{Math.floor(clipForm.start_seconds / 60)}:{(clipForm.start_seconds % 60).toString().padStart(2, '0')}</span>
                </div>
              </div>
              <div>
                <Label>End Time (seconds)</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} step={1} value={clipForm.end_seconds} onChange={e => setClipForm({...clipForm, end_seconds: Math.max(0, parseInt(e.target.value) || 0)})} data-testid="clip-end-input" />
                  <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{Math.floor(clipForm.end_seconds / 60)}:{(clipForm.end_seconds % 60).toString().padStart(2, '0')}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4B5563] bg-[#F3F4F6] p-2 rounded">
              <Clock className="w-3.5 h-3.5 text-[#FF9500]" />
              Duration: {Math.max(0, clipForm.end_seconds - clipForm.start_seconds)} seconds
              {clipForm.end_seconds - clipForm.start_seconds < 2 && <span className="text-[#FF3B30]">(min 2s)</span>}
              {clipForm.end_seconds - clipForm.start_seconds > 600 && <span className="text-[#FF3B30]">(max 10min)</span>}
            </div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input value={clipTagInput} onChange={e => setClipTagInput(e.target.value)} placeholder="Add tag..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (clipTagInput.trim()) { setClipForm({...clipForm, tags: [...clipForm.tags, clipTagInput.trim()]}); setClipTagInput(''); } } }} />
                <Button variant="outline" size="sm" onClick={() => { if (clipTagInput.trim()) { setClipForm({...clipForm, tags: [...clipForm.tags, clipTagInput.trim()]}); setClipTagInput(''); } }}>Add</Button>
              </div>
              {clipForm.tags.length > 0 && <div className="flex gap-1 flex-wrap mt-2">{clipForm.tags.map((t, i) => <Badge key={i} variant="secondary" className="cursor-pointer text-[10px]" onClick={() => setClipForm({...clipForm, tags: clipForm.tags.filter((_, j) => j !== i)})}>{t} ×</Badge>)}</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsClipOpen(false)}>Cancel</Button>
            <Button className="bg-[#FF9500] hover:bg-[#E88B00] text-white" onClick={handleCreateClip} data-testid="save-clip-btn">
              <Scissors className="w-3.5 h-3.5 mr-1.5" />Generate Clip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Create Breakout Room Dialog */}
      <Dialog open={isBreakoutOpen} onOpenChange={setIsBreakoutOpen}>
        <DialogContent data-testid="create-breakout-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><SplitSquareHorizontal className="w-5 h-5" />Create Breakout Room</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Room Name *</Label><Input value={breakoutForm.name} onChange={(e) => setBreakoutForm({ ...breakoutForm, name: e.target.value })} placeholder="e.g., Group Discussion A" data-testid="breakout-name-input" /></div>
            <div><Label>Duration (minutes)</Label><Input type="number" value={breakoutForm.duration_minutes} onChange={(e) => setBreakoutForm({ ...breakoutForm, duration_minutes: parseInt(e.target.value) || 15 })} data-testid="breakout-duration-input" /></div>
            <div>
              <Label>Assign Attendees</Label>
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                {allUsers.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-[#F9FAFB]">
                    <Checkbox
                      checked={breakoutForm.assigned_user_ids.includes(u.user_id)}
                      onCheckedChange={(v) => {
                        setBreakoutForm(prev => ({
                          ...prev,
                          assigned_user_ids: v ? [...prev.assigned_user_ids, u.user_id] : prev.assigned_user_ids.filter(id => id !== u.user_id)
                        }));
                      }}
                    />
                    <span className="text-sm">{u.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#9CA3AF] mt-1">{breakoutForm.assigned_user_ids.length} selected</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBreakoutOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreateBreakout} data-testid="save-breakout-btn">Create Room</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite to Webinar</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Attendee name" /></div>
            <div><Label>Email *</Label><Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="attendee@example.com" data-testid="invite-email-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInviteOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleInvite} data-testid="send-invite-btn"><Mail className="w-4 h-4 mr-2" />Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates List Dialog */}
      <Dialog open={isTemplateListOpen} onOpenChange={setIsTemplateListOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader><DialogTitle>Webinar Templates</DialogTitle></DialogHeader>
          {templates.length === 0 ? (
            <div className="text-center py-8 text-[#9CA3AF]">
              <Download className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" />
              <p className="text-sm">No templates yet. Save a webinar as a template to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((t) => (
                <div key={t.template_id} className="p-3 border border-[#E5E7EB] rounded-lg hover:border-[#002FA7] transition-colors" data-testid={`template-${t.template_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-sm">{t.name}</h4>
                      {t.description && <p className="text-xs text-[#9CA3AF] mt-0.5">{t.description}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[#FF3B30]" onClick={() => handleDeleteTemplate(t.template_id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#9CA3AF] mb-2">
                    <span>{t.duration_minutes}min</span>
                    <span>{t.max_attendees} max</span>
                    {t.preset_polls?.length > 0 && <span>{t.preset_polls.length} polls</span>}
                    <span>Used {t.usage_count}x</span>
                  </div>
                  {t.tags?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mb-2">
                      {t.tags.map((tag, i) => <Badge key={i} variant="secondary" className="text-[10px]">{tag}</Badge>)}
                    </div>
                  )}
                  <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A] w-full" onClick={() => handleCreateFromTemplate(t)} data-testid={`use-template-${t.template_id}`}>
                    <Plus className="w-3 h-3 mr-1" />Use Template
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Annotations Panel component
const EMOJI_MAP = { thumbsup: '👍', heart: '❤️', eyes: '👀', fire: '🔥' };
const TYPE_STYLES = {
  comment: { bg: 'bg-[#002FA7]/5', border: 'border-[#002FA7]/20', icon: MessageCircle, color: 'text-[#002FA7]' },
  note: { bg: 'bg-[#FF9500]/5', border: 'border-[#FF9500]/20', icon: FileText, color: 'text-[#FF9500]' },
  highlight: { bg: 'bg-[#34C759]/5', border: 'border-[#34C759]/20', icon: Sparkles, color: 'text-[#34C759]' },
  question: { bg: 'bg-[#AF52DE]/5', border: 'border-[#AF52DE]/20', icon: MessageCircle, color: 'text-[#AF52DE]' },
};

const AnnotationsPanel = ({ annotations, currentUserId, onAdd, onDelete, onReply, onReact, onResolve, onSeek, onClip, annotationInput, setAnnotationInput, annotationType, setAnnotationType, replyInputs, setReplyInputs, videoRef }) => {
  const [expandedReplies, setExpandedReplies] = useState({});

  const getCurrentTime = () => {
    const t = videoRef?.current?.currentTime || 0;
    return `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white border-t border-[#E5E7EB] max-h-[300px] flex flex-col" data-testid="annotations-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-3.5 h-3.5 text-[#002FA7]" />
          <span className="text-xs font-semibold">{annotations.length} Annotation{annotations.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Annotations List */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {annotations.length === 0 && (
          <p className="text-xs text-[#9CA3AF] text-center py-4">No annotations yet. Add one at the current timestamp!</p>
        )}
        {annotations.map((ann) => {
          const style = TYPE_STYLES[ann.type] || TYPE_STYLES.comment;
          const Icon = style.icon;
          return (
            <div key={ann.annotation_id} className={`p-2 rounded-lg border ${style.bg} ${style.border} ${ann.is_resolved ? 'opacity-60' : ''}`} data-testid={`annotation-${ann.annotation_id}`}>
              <div className="flex items-start gap-2">
                <button onClick={() => onSeek(ann.timestamp_seconds)} className="flex-shrink-0 mt-0.5" data-testid={`annotation-seek-${ann.annotation_id}`}>
                  <span className="text-[10px] font-mono font-bold text-[#002FA7] bg-[#002FA7]/10 px-1.5 py-0.5 rounded hover:bg-[#002FA7]/20 transition-colors cursor-pointer">
                    {ann.timestamp_display}
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3 h-3 ${style.color}`} />
                    <span className="text-[10px] font-medium text-[#0A0A0A]">{ann.user_name}</span>
                    {ann.is_resolved && <CheckCircle className="w-3 h-3 text-[#34C759]" />}
                  </div>
                  <p className="text-xs text-[#4B5563] mt-0.5">{ann.content}</p>

                  {/* Reactions */}
                  <div className="flex items-center gap-1 mt-1.5">
                    {Object.entries(EMOJI_MAP).map(([key, emoji]) => {
                      const count = (ann.reactions?.[key] || []).length;
                      const isReacted = (ann.reactions?.[key] || []).includes(currentUserId);
                      return (
                        <button
                          key={key}
                          onClick={() => onReact(ann.annotation_id, key)}
                          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] transition-colors ${isReacted ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'hover:bg-[#F3F4F6] text-[#9CA3AF]'}`}
                          data-testid={`react-${key}-${ann.annotation_id}`}
                        >
                          <span>{emoji}</span>
                          {count > 0 && <span className="font-medium">{count}</span>}
                        </button>
                      );
                    })}
                    <button onClick={() => onResolve(ann.annotation_id)} className={`ml-1 text-[10px] ${ann.is_resolved ? 'text-[#34C759]' : 'text-[#9CA3AF] hover:text-[#34C759]'}`} data-testid={`resolve-${ann.annotation_id}`}>
                      <CheckCircle className="w-3 h-3" />
                    </button>
                    {onClip && (
                      <button onClick={() => onClip(ann.timestamp_seconds, ann.annotation_id)} className="text-[10px] text-[#9CA3AF] hover:text-[#002FA7]" title="Create clip from here" data-testid={`clip-from-annotation-${ann.annotation_id}`}>
                        <Scissors className="w-3 h-3" />
                      </button>
                    )}
                    {ann.user_id === currentUserId && (
                      <button onClick={() => onDelete(ann.annotation_id)} className="ml-auto text-[10px] text-[#9CA3AF] hover:text-[#FF3B30]" data-testid={`delete-annotation-${ann.annotation_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Replies */}
                  {ann.replies?.length > 0 && (
                    <div className="mt-1.5">
                      <button onClick={() => setExpandedReplies(prev => ({ ...prev, [ann.annotation_id]: !prev[ann.annotation_id] }))} className="text-[10px] text-[#002FA7] hover:underline">
                        {expandedReplies[ann.annotation_id] ? 'Hide' : 'Show'} {ann.replies.length} repl{ann.replies.length === 1 ? 'y' : 'ies'}
                      </button>
                      {expandedReplies[ann.annotation_id] && (
                        <div className="mt-1 pl-2 border-l-2 border-[#E5E7EB] space-y-1">
                          {ann.replies.map((r) => (
                            <div key={r.reply_id} className="text-[10px]">
                              <span className="font-medium text-[#0A0A0A]">{r.user_name}</span>
                              <span className="text-[#4B5563] ml-1">{r.content}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reply input */}
                  <div className="flex gap-1 mt-1">
                    <input
                      value={replyInputs[ann.annotation_id] || ''}
                      onChange={(e) => setReplyInputs(prev => ({ ...prev, [ann.annotation_id]: e.target.value }))}
                      placeholder="Reply..."
                      className="flex-1 text-[10px] border border-[#E5E7EB] rounded px-2 py-0.5 focus:outline-none focus:border-[#002FA7]"
                      onKeyDown={(e) => e.key === 'Enter' && onReply(ann.annotation_id)}
                      data-testid={`reply-input-${ann.annotation_id}`}
                    />
                    <button onClick={() => onReply(ann.annotation_id)} className="text-[#002FA7]"><Send className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Annotation Input */}
      <div className="px-3 py-2 border-t border-[#E5E7EB] bg-[#F9FAFB]">
        <div className="flex items-center gap-1 mb-1.5">
          <span className="text-[10px] text-[#9CA3AF] font-mono">@ {getCurrentTime()}</span>
          <div className="flex gap-0.5 ml-auto">
            {Object.entries(TYPE_STYLES).map(([key, s]) => {
              const Icon = s.icon;
              return (
                <button
                  key={key}
                  onClick={() => setAnnotationType(key)}
                  className={`p-1 rounded text-[10px] transition-colors ${annotationType === key ? `${s.bg} ${s.color}` : 'text-[#9CA3AF] hover:text-[#4B5563]'}`}
                  title={key}
                  data-testid={`annotation-type-${key}`}
                >
                  <Icon className="w-3 h-3" />
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex gap-1">
          <input
            value={annotationInput}
            onChange={(e) => setAnnotationInput(e.target.value)}
            placeholder={`Add ${annotationType}...`}
            className="flex-1 text-xs border border-[#E5E7EB] rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#002FA7]"
            onKeyDown={(e) => e.key === 'Enter' && onAdd()}
            data-testid="annotation-input"
          />
          <button onClick={onAdd} className="bg-[#002FA7] text-white rounded-lg px-3 py-1.5 text-xs hover:bg-[#00227A] transition-colors" data-testid="add-annotation-btn">
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};


// Chapter Timeline bar component
const ChapterTimeline = ({ chapters, totalSeconds, onSeek }) => {
  if (!chapters?.length || !totalSeconds) return null;

  return (
    <div className="bg-[#111] px-3 py-2" data-testid="chapter-timeline">
      <div className="flex items-center gap-2 mb-1.5">
        <ListChecks className="w-3 h-3 text-[#FF9500]" />
        <span className="text-[10px] text-white/60 font-medium uppercase tracking-wider">Chapters</span>
      </div>
      <div className="relative h-6 bg-[#222] rounded-full overflow-hidden">
        {chapters.map((ch, i) => {
          const startPct = (ch.start_seconds / totalSeconds) * 100;
          const endPct = i < chapters.length - 1
            ? (chapters[i + 1].start_seconds / totalSeconds) * 100
            : 100;
          const widthPct = endPct - startPct;
          const colors = ['#002FA7', '#34C759', '#FF9500', '#AF52DE', '#FF3B30', '#007AFF', '#5856D6', '#FF2D55'];
          const color = colors[i % colors.length];

          return (
            <button
              key={i}
              onClick={() => onSeek(ch.start_seconds)}
              className="absolute top-0 h-full group cursor-pointer transition-opacity hover:opacity-90"
              style={{ left: `${startPct}%`, width: `${widthPct}%`, backgroundColor: color + '40', borderRight: '1px solid #111' }}
              title={`${ch.timestamp} — ${ch.title}`}
              data-testid={`chapter-marker-${i}`}
            >
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#0A0A0A] text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10 shadow-lg">
                <span className="text-[#FF9500] font-mono mr-1">{ch.timestamp}</span>{ch.title}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {chapters.map((ch, i) => (
          <button
            key={i}
            onClick={() => onSeek(ch.start_seconds)}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            data-testid={`chapter-btn-${i}`}
          >
            <span className="text-[#FF9500] font-mono">{ch.timestamp}</span>
            <span className="truncate max-w-[120px]">{ch.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Transcript Viewer component
const TranscriptViewer = ({ data, onClose, onSeek }) => {
  const [expandedSections, setExpandedSections] = useState({ chapters: true, summary: true, topics: true, actions: true, quotes: false, transcript: false });

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="border border-[#002FA7]/20 rounded-lg bg-[#F9FAFB] overflow-hidden" data-testid="transcript-viewer">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-[#002FA7]/5 border-b border-[#002FA7]/10">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#002FA7]" />
          <h4 className="font-semibold text-sm text-[#0A0A0A]">AI Transcript Analysis</h4>
          {data.sentiment && (
            <Badge variant="secondary" className={`text-[9px] ${data.sentiment === 'positive' ? 'bg-[#34C759]/10 text-[#34C759]' : data.sentiment === 'negative' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-[#9CA3AF]/10 text-[#4B5563]'}`}>
              {data.sentiment}
            </Badge>
          )}
          {data.chapters?.length > 0 && (
            <Badge variant="secondary" className="text-[9px] bg-[#FF9500]/10 text-[#FF9500]">
              {data.chapters.length} chapters
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}><X className="w-3.5 h-3.5" /></Button>
      </div>

      <div className="p-3 space-y-3 max-h-[400px] overflow-y-auto">
        {/* Chapters */}
        {data.chapters?.length > 0 && (
          <div>
            <button onClick={() => toggleSection('chapters')} className="flex items-center gap-2 w-full text-left">
              <ListChecks className="w-3.5 h-3.5 text-[#FF9500]" />
              <span className="text-xs font-semibold text-[#0A0A0A] flex-1">Chapters ({data.chapters.length})</span>
              {expandedSections.chapters ? <ChevronUp className="w-3 h-3 text-[#9CA3AF]" /> : <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />}
            </button>
            {expandedSections.chapters && (
              <div className="mt-1.5 pl-5 space-y-1" data-testid="transcript-chapters">
                {data.chapters.map((ch, i) => (
                  <button
                    key={i}
                    onClick={() => onSeek && onSeek(ch.start_seconds)}
                    className="flex items-center gap-2 w-full text-left p-1.5 rounded hover:bg-[#002FA7]/5 transition-colors group"
                    data-testid={`chapter-item-${i}`}
                  >
                    <span className="text-[#FF9500] font-mono text-xs font-medium w-10 flex-shrink-0">{ch.timestamp}</span>
                    <span className="text-xs text-[#0A0A0A] group-hover:text-[#002FA7] transition-colors">{ch.title}</span>
                    {onSeek && <Play className="w-3 h-3 text-[#9CA3AF] group-hover:text-[#002FA7] ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        {data.summary && (
          <div>
            <button onClick={() => toggleSection('summary')} className="flex items-center gap-2 w-full text-left">
              <Sparkles className="w-3.5 h-3.5 text-[#002FA7]" />
              <span className="text-xs font-semibold text-[#0A0A0A] flex-1">AI Summary</span>
              {expandedSections.summary ? <ChevronUp className="w-3 h-3 text-[#9CA3AF]" /> : <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />}
            </button>
            {expandedSections.summary && (
              <p className="text-xs text-[#4B5563] mt-1.5 pl-5 leading-relaxed" data-testid="transcript-summary">{data.summary}</p>
            )}
          </div>
        )}

        {/* Key Topics */}
        {data.key_topics?.length > 0 && (
          <div>
            <button onClick={() => toggleSection('topics')} className="flex items-center gap-2 w-full text-left">
              <Tag className="w-3.5 h-3.5 text-[#FF9500]" />
              <span className="text-xs font-semibold text-[#0A0A0A] flex-1">Key Topics ({data.key_topics.length})</span>
              {expandedSections.topics ? <ChevronUp className="w-3 h-3 text-[#9CA3AF]" /> : <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />}
            </button>
            {expandedSections.topics && (
              <div className="flex flex-wrap gap-1.5 mt-1.5 pl-5" data-testid="transcript-topics">
                {data.key_topics.map((t, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] bg-[#FF9500]/10 text-[#FF9500]">{t}</Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Items */}
        {data.action_items?.length > 0 && (
          <div>
            <button onClick={() => toggleSection('actions')} className="flex items-center gap-2 w-full text-left">
              <ListChecks className="w-3.5 h-3.5 text-[#34C759]" />
              <span className="text-xs font-semibold text-[#0A0A0A] flex-1">Action Items ({data.action_items.length})</span>
              {expandedSections.actions ? <ChevronUp className="w-3 h-3 text-[#9CA3AF]" /> : <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />}
            </button>
            {expandedSections.actions && (
              <div className="mt-1.5 pl-5 space-y-1" data-testid="transcript-actions">
                {data.action_items.map((item, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs text-[#4B5563]">
                    <span className="text-[#34C759] font-bold mt-0.5">-</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Key Quotes */}
        {data.key_quotes?.length > 0 && (
          <div>
            <button onClick={() => toggleSection('quotes')} className="flex items-center gap-2 w-full text-left">
              <Quote className="w-3.5 h-3.5 text-[#AF52DE]" />
              <span className="text-xs font-semibold text-[#0A0A0A] flex-1">Key Quotes ({data.key_quotes.length})</span>
              {expandedSections.quotes ? <ChevronUp className="w-3 h-3 text-[#9CA3AF]" /> : <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />}
            </button>
            {expandedSections.quotes && (
              <div className="mt-1.5 pl-5 space-y-1.5" data-testid="transcript-quotes">
                {data.key_quotes.map((q, i) => (
                  <div key={i} className="text-xs text-[#4B5563] italic border-l-2 border-[#AF52DE]/30 pl-2">"{q}"</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Full Transcript with Timestamps */}
        {data.transcript && (
          <div>
            <button onClick={() => toggleSection('transcript')} className="flex items-center gap-2 w-full text-left">
              <FileText className="w-3.5 h-3.5 text-[#4B5563]" />
              <span className="text-xs font-semibold text-[#0A0A0A] flex-1">Full Transcript</span>
              {expandedSections.transcript ? <ChevronUp className="w-3 h-3 text-[#9CA3AF]" /> : <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />}
            </button>
            {expandedSections.transcript && (
              <div className="mt-1.5 pl-5 bg-white p-3 rounded border border-[#E5E7EB] max-h-48 overflow-y-auto" data-testid="transcript-full-text">
                {data.segments?.length > 0 ? (
                  data.segments.map((seg, i) => {
                    const mins = Math.floor(seg.start / 60);
                    const secs = Math.floor(seg.start % 60);
                    return (
                      <div key={i} className="flex gap-2 text-xs mb-1 group">
                        <button
                          onClick={() => onSeek && onSeek(seg.start)}
                          className="text-[#002FA7] font-mono text-[10px] w-10 flex-shrink-0 hover:underline cursor-pointer"
                        >
                          {`${mins}:${secs.toString().padStart(2, '0')}`}
                        </button>
                        <span className="text-[#4B5563] leading-relaxed">{seg.text}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-xs text-[#4B5563] leading-relaxed whitespace-pre-wrap">{data.transcript}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Q&A Question Card component
const QACard = ({ q, isPinned, isAnswered, isHost, userId, onUpvote, onAction, answeringId, setAnsweringId, answerInput, setAnswerInput }) => (
  <div className={`p-2 rounded-lg text-xs ${isPinned ? 'bg-[#002FA7]/5 border border-[#002FA7]/20' : isAnswered ? 'bg-[#34C759]/5' : 'bg-[#F3F4F6]'}`} data-testid={`qa-card-${q.question_id}`}>
    <div className="flex items-start gap-2">
      <button onClick={() => onUpvote(q.question_id)} className={`flex flex-col items-center pt-0.5 ${q.upvoted_by?.includes(userId) ? 'text-[#002FA7]' : 'text-[#9CA3AF]'}`} data-testid={`upvote-${q.question_id}`}>
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
        <span className="text-[10px] font-bold">{q.upvotes}</span>
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[#0A0A0A]">{q.content}</p>
        <p className="text-[10px] text-[#9CA3AF] mt-0.5">{q.user_name}{isPinned && ' · Pinned'}</p>
        {q.is_answered && q.answer_text && (
          <div className="mt-1.5 p-1.5 bg-white rounded border-l-2 border-[#34C759]">
            <p className="text-[#4B5563]">{q.answer_text}</p>
            <p className="text-[10px] text-[#9CA3AF]">Answered by {q.answered_by}</p>
          </div>
        )}
        {answeringId === q.question_id && (
          <div className="mt-1.5 flex gap-1">
            <input value={answerInput} onChange={(e) => setAnswerInput(e.target.value)} placeholder="Type your answer..." className="flex-1 text-xs border rounded px-2 py-1" onKeyDown={(e) => e.key === 'Enter' && onAction(q.question_id, 'answer', answerInput)} />
            <button onClick={() => onAction(q.question_id, 'answer', answerInput)} className="text-[#002FA7] font-medium text-[10px]">Send</button>
          </div>
        )}
      </div>
    </div>
    {isHost && !q.is_answered && (
      <div className="flex gap-1 mt-1.5 ml-6">
        <button onClick={() => onAction(q.question_id, 'pin')} className="text-[10px] text-[#002FA7] hover:underline">{q.is_pinned ? 'Unpin' : 'Pin'}</button>
        <span className="text-[#E5E7EB]">·</span>
        <button onClick={() => setAnsweringId(q.question_id)} className="text-[10px] text-[#34C759] hover:underline">Answer</button>
        <span className="text-[#E5E7EB]">·</span>
        <button onClick={() => onAction(q.question_id, 'dismiss')} className="text-[10px] text-[#FF3B30] hover:underline">Dismiss</button>
      </div>
    )}
  </div>
);

export default WebinarsPage;
